// src/components/shared/Navbar.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import logo from "../../logos/sp-logo.jpg";

export default function Navbar () {
  const { currentUser, userRole, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  const isAdmin = userRole === "admin";
  const dashPath = isAdmin ? "/admin" : "/dashboard";

  const navStyle = {
    position: "sticky",
    top: 0,
    zIndex: 100,
    background: scrolled ? "rgba(10,10,10,0.97)" : "var(--sp-black)",
    backdropFilter: "blur(12px)",
    borderBottom: scrolled ? "1px solid rgba(255,214,0,0.15)" : "1px solid transparent",
    transition: "all 0.3s ease",
    fontFamily: "var(--font-body)"
  };

  const innerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    height: 64,
    maxWidth: 1200,
    margin: "0 auto"
  };

  const logoStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    textDecoration: "none"
  };

  const logoIconStyle = {
    width: 36,
    height: 36,
    background: "var(--sp-yellow)",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    fontWeight: 700,
    color: "var(--sp-black)"
  };

  const logoTextStyle = {
    fontFamily: "var(--font-display)",
    fontSize: 22,
    color: "var(--sp-white)",
    letterSpacing: "0.04em"
  };

  const logoSubStyle = {
    fontSize: 9,
    color: "var(--sp-yellow)",
    fontFamily: "var(--font-body)",
    fontWeight: 500,
    letterSpacing: "0.08em",
    lineHeight: 1,
    marginTop: -2
  };

  return (
    <nav style={navStyle}>
      <div style={innerStyle}>
        {/* Logo */}
        <Link to={currentUser ? dashPath : "/"} style={logoStyle}>
          <div style={logoIconStyle}>
            <img src={logo} alt="Science Point logo" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
          </div>
          <div>
            <div style={logoTextStyle}>SCIENCE POINT</div>
            <div style={logoSubStyle}>ACADEMIC &amp; ADMISSION CARE</div>
          </div>
        </Link>

        {/* Desktop nav */}
        {currentUser && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Nav links */}
            <div style={{ display: "flex", gap: 4, marginRight: 8 }}>
              {isAdmin ? (
                <>
                  <NavLink to="/admin" label="Dashboard" current={location.pathname} />
                  <NavLink to="/admin/videos" label="Videos" current={location.pathname} />
                  <NavLink to="/admin/students" label="Students" current={location.pathname} />
                </>
              ) : (
                <>
                  <NavLink to="/dashboard" label="Home" current={location.pathname} />
                  <NavLink to="/dashboard/library" label="Library" current={location.pathname} />
                  <NavLink to="/dashboard/progress" label="My Progress" current={location.pathname} />
                </>
              )}
            </div>

            {/* User badge */}
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 14px",
              background: "rgba(255,214,0,0.1)",
              border: "1px solid rgba(255,214,0,0.25)",
              borderRadius: 99,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: "var(--sp-yellow)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700, color: "var(--sp-black)"
              }}>
                {currentUser.name?.[0]?.toUpperCase() || "U"}
              </div>
              <span style={{ fontSize: 13, color: "var(--sp-white)", fontWeight: 500 }}>
                {currentUser.name?.split(" ")[0]}
              </span>
              {isAdmin && (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: "var(--sp-black)",
                  background: "var(--sp-yellow)", padding: "2px 7px", borderRadius: 99, letterSpacing: "0.05em"
                }}>ADMIN</span>
              )}
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              style={{
                padding: "8px 16px", fontSize: 13, fontWeight: 600,
                background: "transparent", color: "var(--sp-gray-light)",
                border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8,
                transition: "all var(--transition)",
                fontFamily: "var(--font-body)"
              }}
              onMouseEnter={e => { e.target.style.color = "#fff"; e.target.style.borderColor = "rgba(255,255,255,0.3)"; }}
              onMouseLeave={e => { e.target.style.color = "var(--sp-gray-light)"; e.target.style.borderColor = "rgba(255,255,255,0.1)"; }}
            >
              Logout
            </button>
          </div>
        )}

        {!currentUser && (
          <Link
            to="/login"
            style={{
              padding: "9px 20px",
              background: "var(--sp-yellow)",
              color: "var(--sp-black)",
              borderRadius: 8, fontWeight: 700, fontSize: 14,
              transition: "all var(--transition)"
            }}
            onMouseEnter={e => { e.target.style.background = "var(--sp-yellow-dark)"; e.target.style.transform = "translateY(-1px)"; }}
            onMouseLeave={e => { e.target.style.background = "var(--sp-yellow)"; e.target.style.transform = "none"; }}
          >
            Sign In
          </Link>
        )}
      </div>
    </nav>
  );
}

function NavLink({ to, label, current }) {
  const active = current === to || current.startsWith(to + "/");
  return (
    <Link
      to={to}
      style={{
        padding: "6px 14px", fontSize: 13, fontWeight: active ? 600 : 400,
        color: active ? "var(--sp-yellow)" : "var(--sp-gray-light)",
        background: active ? "rgba(255,214,0,0.1)" : "transparent",
        border: active ? "1px solid rgba(255,214,0,0.2)" : "1px solid transparent",
        borderRadius: 8, transition: "all var(--transition)", textDecoration: "none"
      }}
      onMouseEnter={e => {
        if (!active) {
          e.currentTarget.style.color = "#fff";
          e.currentTarget.style.background = "rgba(255,255,255,0.06)";
        }
      }}
      onMouseLeave={e => {
        if (!active) {
          e.currentTarget.style.color = "var(--sp-gray-light)";
          e.currentTarget.style.background = "transparent";
        }
      }}
    >
      {label}
    </Link>
  );
}
